import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
  Loader2, MessageCircle, Mail, Phone, ArrowRight, FileText, Send, Check, X,
  Clock, Bell, Receipt, Euro, RotateCcw, Calendar, UserPlus, Users, History,
} from "lucide-react";

const ICONS = {
  message: MessageCircle,
  mail: Mail,
  phone: Phone,
  "arrow-right": ArrowRight,
  "file-text": FileText,
  send: Send,
  check: Check,
  x: X,
  clock: Clock,
  bell: Bell,
  receipt: Receipt,
  euro: Euro,
  refund: RotateCcw,
  calendar: Calendar,
  "user-plus": UserPlus,
  user: Users,
};

const TYPE_COLORS = {
  interaction: "text-slate-300",
  email: "text-[#C4B5FD]",
  status_change: "text-[#F0C572]",
  assignment: "text-slate-300",
  quote_created: "text-[#E07A62]",
  quote_sent: "text-[#E07A62]",
  quote_signed: "text-[#B8C784]",
  quote_refused: "text-[#E67B6D]",
  quote_expired: "text-slate-500",
  reminder_sent: "text-[#F0C572]",
  invoice_issued: "text-[#E07A62]",
  invoice_paid: "text-[#B8C784]",
  credit_note: "text-[#E67B6D]",
  rendezvous: "text-[#D99A2B]",
  lead_created: "text-slate-500",
};

const FILTERS = [
  { key: "interaction", label: "Notes & appels" },
  { key: "email", label: "Emails" },
  { key: "status_change", label: "Statut" },
  { key: "quote", label: "Devis" },
  { key: "invoice", label: "Factures" },
  { key: "reminder_sent", label: "Relances" },
  { key: "credit_note", label: "Notes de crédit" },
  { key: "rendezvous", label: "RDV" },
];

function matchFilter(evtType, filterKey) {
  if (filterKey === "quote") return evtType.startsWith("quote_");
  if (filterKey === "invoice") return evtType.startsWith("invoice_");
  return evtType === filterKey;
}

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleString("fr-BE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function LeadTimeline({ leadId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(() => new Set(FILTERS.map((f) => f.key)));

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/leads/${leadId}/timeline`);
        if (alive) setEvents(data.events || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [leadId]);

  const shown = useMemo(
    () => events.filter((e) => [...enabled].some((k) => matchFilter(e.type, k))),
    [events, enabled],
  );

  const toggle = (key) => {
    setEnabled((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="lead-timeline">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <History className="h-4 w-4 text-[#C4553B]" />
            <span className="text-sm font-semibold text-slate-200">Timeline d&apos;activité</span>
          </div>
          <span className="text-xs text-slate-500" data-testid="timeline-count">
            {shown.length}/{events.length}
          </span>
        </div>

        <div className="mb-3 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const on = enabled.has(f.key);
            return (
              <button
                key={f.key}
                onClick={() => toggle(f.key)}
                className={`rounded-full border px-2 py-0.5 text-[11px] transition ${
                  on
                    ? "border-[#C4553B]/60 bg-[#C4553B]/15 text-[#E07A62]"
                    : "border-slate-700 bg-slate-950/40 text-slate-500 hover:text-slate-300"
                }`}
                data-testid={`timeline-filter-${f.key}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        ) : shown.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-500" data-testid="timeline-empty">
            Aucun événement pour ces filtres.
          </div>
        ) : (
          <ol className="relative border-l border-slate-800 pl-4">
            {shown.map((e) => {
              const Icon = ICONS[e.icon_key] || MessageCircle;
              const color = TYPE_COLORS[e.type] || "text-slate-400";
              return (
                <li key={e.id} className="mb-4 last:mb-0" data-testid={`timeline-event-${e.type}`}>
                  <span className="absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full border border-slate-700 bg-slate-950">
                    <Icon className={`h-2.5 w-2.5 ${color}`} />
                  </span>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className={`text-xs font-semibold ${color}`}>{e.label}</span>
                    <span className="text-[10px] text-slate-500">{fmtWhen(e.at)}</span>
                  </div>
                  {e.preview && (
                    <p className="mt-0.5 text-xs text-slate-400 leading-snug">{e.preview}</p>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
